import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import api from "../../../utils/api";
import { useRestaurantConfig } from "../../../utils/restaurantConfig";
import { getCurrencySymbol } from "../../../helper/formatCurrency";

const inputClass =
  "w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-black placeholder-gray-400 focus:outline-none focus:border-black focus:bg-white transition-all duration-200";

const labelClass =
  "block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2";

const EditSaleModal = ({ open, onClose }) => {
  const { locale } = useRestaurantConfig();
  const currencySymbol = getCurrencySymbol(locale);

  const [selectedDate, setSelectedDate] = useState("");
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [successId, setSuccessId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  useEffect(() => {
    if (!open) {
      setSelectedDate("");
      setSalesData([]);
    }
  }, [open]);

  useEffect(() => {
    if (selectedDate) {
      fetchSalesByDate(selectedDate);
    } else {
      setSalesData([]);
    }
  }, [selectedDate]);

  const fetchSalesByDate = async (date) => {
    try {
      setLoading(true);
      const res = await api.get(`/other-apps/daily-sales/by-date?date=${date}`);
      if (res.data?.success) {
        setSalesData(res.data.dailySales || []);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch sales for selected date");
      setSalesData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (id, field, value) => {
    setSalesData((prev) =>
      prev.map((sale) => (sale._id === id ? { ...sale, [field]: value } : sale))
    );
  };

  const handleUpdate = async (sale) => {
    if (!sale.totalSale?.toString().trim() || !sale.totalOrders?.toString().trim()) {
      toast.error("Total Sale and Total Orders cannot be empty");
      return;
    }

    try {
      setSavingId(sale._id);
      const res = await api.put(`/other-apps/daily-sales/${sale._id}`, {
        totalSale: Number(sale.totalSale),
        totalOrders: Number(sale.totalOrders),
      });
      if (res.data?.success) {
        setSuccessId(sale._id);
        setTimeout(() => setSuccessId(null), 3000);
      }
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.message || "Failed to update sale";
      toast.error(msg);
    } finally {
      setSavingId(null);
    }
  };

  const handleDeleteClick = (id, appName) => {
    setDeleteTarget({ id, appName });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      setLoading(true);
      const res = await api.delete(`/other-apps/daily-sales/${deleteTarget.id}`);
      if (res.data?.success) {
        toast.success("Sale record removed");
        fetchSalesByDate(selectedDate);
      }
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.message || "Failed to delete sale";
      toast.error(msg);
    } finally {
      setLoading(false);
      setDeleteTarget(null);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="absolute inset-0 bg-black/50" />

      <div className="relative w-full max-w-4xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="bg-black px-8 py-6 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-white text-lg font-bold">Edit App Sale</h2>
            <p className="text-gray-400 text-xs mt-0.5">
              Select a date to view and edit daily sales
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-8 overflow-y-auto flex-1">
          <div className="mb-6 max-w-sm">
            <label className={labelClass}>Select Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={today}
              className={inputClass}
            />
          </div>

          {loading && !salesData.length && selectedDate && (
            <div className="text-sm text-gray-500 py-4">Loading sales data...</div>
          )}

          {!loading && selectedDate && salesData.length === 0 && (
            <div className="text-sm text-gray-500 border border-dashed border-gray-200 rounded-xl p-4 text-center">
              No sales records found for this date.
            </div>
          )}

          {salesData.length > 0 && (
            <div className="space-y-4">
              {salesData.map((sale) => {
                const user = sale.updatedBy?.username || sale.createdBy?.username || "System";
                const date = new Date(sale.updatedAt || sale.createdAt).toLocaleString(undefined, {
                  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                });

                return (
                  <div key={sale._id} className="grid grid-cols-1 md:grid-cols-[1fr_1.5fr_1fr_auto_auto] gap-4 items-end border border-gray-100 rounded-xl p-4 bg-gray-50/50">
                    <div>
                      <label className={labelClass}>App Name</label>
                      <div className="h-[46px] flex items-center text-base font-bold text-gray-900 px-1">
                        {sale.appName}
                      </div>
                    </div>
                    
                    <div>
                      <label className={labelClass}>Total Sale</label>
                      <div className="relative flex items-center">
                        <span className="absolute left-4 text-gray-500 font-medium select-none pointer-events-none">
                          {currencySymbol}
                        </span>
                        <input
                          type="text"
                          value={sale.totalSale}
                          onChange={(e) => handleFieldChange(sale._id, "totalSale", e.target.value.replace(/\D/g, ""))}
                          className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm text-black placeholder-gray-400 focus:outline-none focus:border-black transition-all duration-200"
                          inputMode="numeric"
                        />
                      </div>
                    </div>

                    <div>
                      <label className={labelClass}>Total Orders</label>
                      <input
                        type="text"
                        value={sale.totalOrders}
                        onChange={(e) => handleFieldChange(sale._id, "totalOrders", e.target.value.replace(/\D/g, ""))}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm text-black placeholder-gray-400 focus:outline-none focus:border-black transition-all duration-200"
                        inputMode="numeric"
                      />
                    </div>

                    <div className="flex flex-col h-[46px] justify-center relative">
                      <button
                        onClick={() => handleUpdate(sale)}
                        disabled={savingId === sale._id || successId === sale._id}
                        className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 whitespace-nowrap ${
                          successId === sale._id
                            ? "bg-green-100 text-green-700"
                            : savingId === sale._id
                            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700 text-white active:scale-[0.98]"
                        }`}
                      >
                        {successId === sale._id ? "Updated Successfully" : savingId === sale._id ? "Saving..." : "Update"}
                      </button>
                    </div>

                    <div className="flex flex-col h-[46px] justify-center">
                      <button
                        onClick={() => handleDeleteClick(sale._id, sale.appName)}
                        className="p-2.5 text-red-500 hover:bg-red-50 rounded-xl transition-colors duration-200"
                        title="Delete record"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>

                    <div className="md:col-span-5 text-[11px] text-gray-400 font-medium text-right mt-1">
                      Last updated by <span className="text-gray-600">{user}</span> on {date}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Custom Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDeleteTarget(null)} />
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden p-8 text-center animate-in fade-in zoom-in-95 duration-200">
            <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Remove sale record?</h3>
            <p className="text-sm text-gray-500 mb-8">
              This will remove the sale for <span className="font-bold text-gray-900">{deleteTarget.appName}</span> on{" "}
              <span className="font-bold text-gray-900">
                {new Date(selectedDate).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
              </span>
              . The row is hidden from reports and you can add a new entry for that date later.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 py-3 bg-gray-50 hover:bg-gray-100 text-gray-700 font-semibold rounded-xl transition-all duration-200 border border-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-all duration-200 active:scale-[0.98]"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default EditSaleModal;
